import { IntlErrorCode } from '@formatjs/intl'
import useLocalStorage from '@hooks/useLocalStorage'
import { findValueInEnum } from '@utils/enum'
import type { PropsWithChildren } from 'react'
import React from 'react'
import { IntlProvider } from 'react-intl'

export enum Locale {
  EN = 'en',
  ID = 'id',
}

const localeLanguages: Partial<Record<Locale, any>> = {
  [Locale.EN]: import('../../../locale/en.json'),
  [Locale.ID]: import('../../../locale/id.json'),
}
export const SupportedLocale: Locale[] = Object.keys(localeLanguages).map((i) =>
  findValueInEnum(i, Locale),
)

interface StructuredMessage {
  description?: string
  defaultMessage: string
}
type LocaleMessages = Record<string, StructuredMessage>

export const localeNames: Record<Locale, string> = {
  [Locale.EN]: 'English',
  [Locale.ID]: 'Bahasa Indonesia',
}

const dotSeparator = '_dot_'
const sepRegExp = new RegExp(dotSeparator, 'g')

function getKeyValueJson(messages: LocaleMessages | undefined): Record<string, string> | undefined {
  if (messages) {
    const keyValueMessages: Record<string, string> = {}
    return Object.entries(messages).reduce((acc, [id, msg]) => {
      acc[id.replace(sepRegExp, '.')] = msg.defaultMessage
      return acc
    }, keyValueMessages)
  }

  return undefined
}

export function getMatchingLocale(languages: readonly string[]): Locale | undefined {
  const localeEntries: Array<[string, Locale]> = Object.entries(Locale).filter(([_, loc]) =>
    Object.keys(localeLanguages).includes(loc),
  )

  for (const preferredLocale of languages) {
    for (const localeEntry of localeEntries) {
      if (localeEntry[1].toLowerCase() === preferredLocale.toLowerCase()) {
        return Locale[localeEntry[0] as keyof typeof Locale]
      }
    }
  }

  return undefined
}

const defaultLocale = Locale.EN

export interface LocaleContextType {
  locale: Locale
  setLocale: (locale: Locale) => void
}
export const LocaleContext = React.createContext<LocaleContextType>({
  locale: defaultLocale,
  setLocale: () => undefined,
})

const LocaleProvider: React.FC<PropsWithChildren<NonNullable<unknown>>> = ({ children }) => {
  const [locale, setLocale] = useLocalStorage(
    'locale',
    getMatchingLocale(navigator.languages) || defaultLocale,
  )

  const [messages, setMessages] = React.useState<LocaleMessages | undefined>(undefined)

  React.useEffect(() => {
    async function changeLocale() {
      if (locale) {
        const mod = await localeLanguages[locale]
        setMessages(mod.default)
      }
    }

    changeLocale()
  }, [locale])

  return (
    <IntlProvider
      defaultLocale={defaultLocale}
      locale={locale}
      messages={getKeyValueJson(messages)}
      onError={(err) => {
        if (import.meta.env.DEV && err.code === IntlErrorCode.MISSING_TRANSLATION) {
          console.debug(err)
        }
      }}
      key={locale}
    >
      <LocaleContext
        value={{
          locale,
          setLocale,
        }}
      >
        {children}
      </LocaleContext>
    </IntlProvider>
  )
}

export { LocaleProvider }
